// src/services/api/paymentAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Payment API service for communication with Firebase functions
 */
export class PaymentAPI {
  
  /**
   * Create a payment link for an appointment
   * @param {Object} paymentData - Payment details
   * @returns {Promise<Object>} Payment link response
   */
  static async createPaymentLink(paymentData) {
    try {
      console.log('PaymentAPI: Creating payment link', {
        appointmentId: paymentData.appointmentId,
        customerName: paymentData.customerName,
        amount: paymentData.price
      });

      // Validate required fields
      this.validatePaymentData(paymentData);

      const headers = await createAuthHeaders();

      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'createPaymentLink',
          ...paymentData
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create payment link');
      }

      console.log('PaymentAPI: Payment link created successfully', {
        appointmentId: paymentData.appointmentId,
        paymentId: result.data.paymentId,
        expiresAt: result.data.expiresAt
      });

      return result.data;

    } catch (error) {
      console.error('PaymentAPI: Error creating payment link:', error);
      throw new Error(`Failed to create payment link: ${error.message}`);
    }
  }

  /**
   * Create a batch payment link for multiple appointments
   * @param {Object} batchData - Batch payment details
   * @param {Array<string>} batchData.appointmentIds - Array of appointment IDs
   * @param {string} batchData.customerName - Customer name
   * @param {string} batchData.customerPhone - Customer phone (optional)
   * @param {string} batchData.customerEmail - Customer email (optional)
   * @returns {Promise<Object>} Batch payment link response
   */
  static async createBatchPaymentLink(batchData) {
    try {
      console.log('PaymentAPI: Creating batch payment link', {
        appointmentCount: batchData.appointmentIds?.length,
        customerName: batchData.customerName
      });

      // Validate required fields
      if (!batchData.appointmentIds || !Array.isArray(batchData.appointmentIds) || batchData.appointmentIds.length === 0) {
        throw new Error('appointmentIds array is required');
      }

      if (!batchData.customerName) {
        throw new Error('customerName is required');
      }

      const headers = await createAuthHeaders();

      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'createBatchPaymentLink',
          ...batchData
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to create batch payment link');
      }

      console.log('PaymentAPI: Batch payment link created successfully', {
        batchId: result.data.batchId,
        appointmentCount: result.data.appointmentCount,
        totalAmount: result.data.totalAmount,
        expiresAt: result.data.expiresAt
      });

      return result;

    } catch (error) {
      console.error('PaymentAPI: Error creating batch payment link:', error);
      throw new Error(`Failed to create batch payment link: ${error.message}`);
    }
  }

  /**
   * Get payment status for an appointment
   * @param {string} userId - User ID
   * @param {string} appointmentId - Appointment ID
   * @returns {Promise<Array>} Array of payment attempts
   */
  static async getPaymentStatus(userId, appointmentId) {
    try {
      console.log('PaymentAPI: Getting payment status', { userId: userId?.substring(0, 8) + '***', appointmentId });

      if (!userId || !appointmentId) {
        throw new Error('userId and appointmentId are required');
      }

      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'getPaymentStatus',
          userId,
          appointmentId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get payment status');
      }

      console.log('PaymentAPI: Retrieved payment status', {
        appointmentId,
        paymentsCount: result.data?.length || 0
      });

      return result.data || [];
      
    } catch (error) {
      console.error('PaymentAPI: Error getting payment status:', error);
      throw new Error(`Failed to get payment status: ${error.message}`);
    }
  }

  /**
   * Cancel a payment request
   * @param {string} userId - User ID
   * @param {string} paymentId - Payment ID to cancel
   * @returns {Promise<Object>} Cancellation result
   */
  static async cancelPaymentRequest(userId, paymentId) {
    try {
      console.log('PaymentAPI: Cancelling payment request', { 
        userId: userId?.substring(0, 8) + '***', 
        paymentId 
      });

      if (!userId || !paymentId) {
        throw new Error('userId and paymentId are required');
      }

      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'cancelPaymentRequest',
          userId,
          paymentId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel payment request');
      }

      console.log('PaymentAPI: Payment request cancelled successfully', { paymentId });

      return result;
      
    } catch (error) {
      console.error('PaymentAPI: Error cancelling payment request:', error);
      throw new Error(`Failed to cancel payment request: ${error.message}`);
    }
  }

  /**
   * Generate payment context from appointment data
   * @param {Object} appointment - Appointment object
   * @param {string} userId - User ID
   * @returns {Object} Payment context for API call
   */
  static generatePaymentContext(appointment, userId) {
    if (!appointment || !userId) {
      throw new Error('Appointment and userId are required');
    }

    return {
      userId,
      appointmentId: appointment.id,
      customerName: appointment.customer_name,
      customerPhone: appointment.customer_phone || '',
      customerEmail: appointment.customer_email || '',
      serviceName: appointment.service_name,
      price: parseFloat(appointment.price),
      appointmentDate: appointment.date,
      appointmentTime: appointment.time
    };
  }

  /**
   * Validate payment data before API call
   * @param {Object} paymentData - Payment data to validate
   */
  static validatePaymentData(paymentData) {
    const required = ['userId', 'appointmentId', 'customerName', 'serviceName', 'price'];
    const missing = required.filter(field => !paymentData[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }

    if (isNaN(paymentData.price) || paymentData.price <= 0) {
      throw new Error('Price must be a positive number');
    }

    if (paymentData.customerName.length < 2) {
      throw new Error('Customer name must be at least 2 characters');
    }

    if (paymentData.serviceName.length < 2) {
      throw new Error('Service name must be at least 2 characters');
    }

    // Validate userId format (Firebase UID)
    if (!paymentData.userId || typeof paymentData.userId !== 'string' || paymentData.userId.length < 10) {
      throw new Error('Valid userId is required');
    }

    // Validate appointmentId format 
    if (!paymentData.appointmentId || typeof paymentData.appointmentId !== 'string' || paymentData.appointmentId.length < 10) {
      throw new Error('Valid appointmentId is required');
    }
  }

  /**
   * Handle API errors and provide user-friendly messages
   * @param {Error} error - The error to handle
   * @returns {string} User-friendly error message
   */
  static getErrorMessage(error) {
    const message = error.message || 'Unknown error';
    
    // Map technical errors to user-friendly Hebrew messages
    const errorMappings = {
      'Missing required fields': 'חסרים שדות חובה',
      'Valid userId is required': 'משתמש לא תקין',
      'Valid appointmentId is required': 'מזהה תור לא תקין',
      'Price must be a positive number': 'מחיר חייב להיות מספר חיובי',
      'Customer name must be at least 2 characters': 'שם לקוח חייב להכיל לפחות 2 תווים',
      'Service name must be at least 2 characters': 'שם שירות חייב להכיל לפחות 2 תווים',
      'HTTP 400': 'בקשה לא תקינה',
      'HTTP 401': 'נדרשת הרשאה',
      'HTTP 403': 'אין הרשאה לביצוע פעולה זו',
      'HTTP 500': 'שגיאה בשרת',
      'HTTP 502': 'שגיאה בשירות חיצוני',
      'HTTP 503': 'השירות אינו זמין כרגע',
      'Failed to fetch': 'בעיית חיבור לשרת',
      'Network request failed': 'בעיית חיבור לאינטרנט'
    };

    // Find matching error mapping
    for (const [key, value] of Object.entries(errorMappings)) {
      if (message.includes(key)) {
        return value;
      }
    }

    // Default Hebrew error message
    return 'אירעה שגיאה במערכת. אנא נסה שוב.';
  }

  /**
   * Retry mechanism for failed API calls
   * @param {Function} apiCall - The API call function to retry
   * @param {number} maxRetries - Maximum number of retries
   * @param {number} delay - Delay between retries in milliseconds
   * @returns {Promise} Result of the API call
   */
  static async retryApiCall(apiCall, maxRetries = 3, delay = 1000) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`PaymentAPI: Attempt ${attempt}/${maxRetries}`);
        const result = await apiCall();
        return result;
      } catch (error) {
        lastError = error;
        console.warn(`PaymentAPI: Attempt ${attempt} failed:`, error.message);

        // Don't retry on validation errors (4xx status codes)
        if (error.message.includes('HTTP 4')) {
          throw error;
        }

        // Wait before retrying (except on last attempt)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }

    throw lastError;
  }

  /**
   * Check if the API is healthy
   * @returns {Promise<boolean>} Whether the API is responsive
   */
  static async healthCheck() {
    try {
      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'healthCheck'
        })
      });

      return response.ok;
    } catch (error) {
      console.warn('PaymentAPI: Health check failed:', error);
      return false;
    }
  }
}